import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface DeviceContractInfo {
  totalYears: number;
  currentYear: number;
  annualCredits: number;
  totalContractCredits: number;
  nextRenewalDate: Date | null;
  isMultiYear: boolean;
  yearsReleased: number;
  yearsReserved: number;
  isLoading: boolean;
}

export const useDeviceContractInfo = (deviceId: string | null | undefined): DeviceContractInfo => {
  const { data, isLoading } = useQuery({
    queryKey: ["device-contract-info", deviceId],
    queryFn: async () => {
      if (!deviceId) return null;

      const { data: packages, error } = await supabase
        .from("device_credit_packages")
        .select("*")
        .eq("device_id", deviceId)
        .order("year_number", { ascending: true });

      if (error) {
        console.error("Error fetching device credit packages:", error);
        return null;
      }

      if (!packages || packages.length === 0) {
        return null;
      }

      // Group by year_number to get unique years
      const yearMap = new Map<number, typeof packages>();
      packages.forEach((pkg) => {
        const year = pkg.year_number || 1;
        if (!yearMap.has(year)) {
          yearMap.set(year, []);
        }
        yearMap.get(year)!.push(pkg);
      });

      const totalYears = yearMap.size;
      
      // Count released and reserved years
      let yearsReleased = 0;
      let yearsReserved = 0;
      let nextRenewalDate: Date | null = null;
      let annualCredits = 0;
      let totalContractCredits = 0;

      // Process each year
      const sortedYears = Array.from(yearMap.keys()).sort((a, b) => a - b);
      
      for (const year of sortedYears) {
        const yearPackages = yearMap.get(year)!;
        const yearCredits = yearPackages.reduce((sum, pkg) => sum + pkg.total_credits, 0);
        totalContractCredits += yearCredits;
        
        // Use first year's credits as annual credits reference
        if (year === sortedYears[0]) {
          annualCredits = yearCredits;
        }

        // Check if year is released
        const isYearReleased = yearPackages.some((pkg) => pkg.is_released === true);
        
        if (isYearReleased) {
          yearsReleased++;
        } else {
          yearsReserved++;
          // Find the next renewal date (earliest unreleased package)
          if (!nextRenewalDate) {
            const earliestRelease = yearPackages
              .filter((pkg) => pkg.release_date)
              .map((pkg) => new Date(pkg.release_date!))
              .sort((a, b) => a.getTime() - b.getTime())[0];
            
            if (earliestRelease) {
              nextRenewalDate = earliestRelease;
            }
          }
        }
      }

      // Current year is the number of released years (minimum 1)
      const currentYear = Math.max(1, yearsReleased);

      return {
        totalYears,
        currentYear,
        annualCredits,
        totalContractCredits,
        nextRenewalDate,
        isMultiYear: totalYears > 1,
        yearsReleased,
        yearsReserved,
      };
    },
    enabled: !!deviceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    totalYears: data?.totalYears || 1,
    currentYear: data?.currentYear || 1,
    annualCredits: data?.annualCredits || 0,
    totalContractCredits: data?.totalContractCredits || 0,
    nextRenewalDate: data?.nextRenewalDate || null,
    isMultiYear: data?.isMultiYear || false,
    yearsReleased: data?.yearsReleased || 0,
    yearsReserved: data?.yearsReserved || 0,
    isLoading,
  };
};
